<template>
  <div>
    <!-- Tabs -->
    <div class="tabs flex border-b mb-4">
      <button
        class="py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue': currentTab === 'lab-results',
          'text-gray-500': currentTab !== 'lab-results',
        }"
        @click="currentTab = 'lab-results'"
      >
        Lab Results
      </button>
      <button
        class="py-2 -mb-px font-semibold border-b-2 px-4"
        :class="{
          'border-custom-yellow text-custom-blue':
            currentTab === 'radiology-results',
          'text-gray-500': currentTab !== 'radiology-results',
        }"
        @click="currentTab = 'radiology-results'"
      >
        Radiology Results
      </button>
    </div>

    <div
      v-if="currentTab === 'lab-results'"
      class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto"
    >
      <div
        v-for="test in completedLabResults"
        :key="test.id"
        class="grid grid-cols-2 gap-6"
      >
        <div>
          <div class="mb-4 flex items-center gap-3">
            <h3 class="text-sm font-semibold text-gray-600">Test Type:</h3>
            <p class="text-sm">{{ test.test_type?.name }}</p>
            <span
              class="text-xs px-2 py-0.5 rounded-full font-medium"
              :class="test.referred_out ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'"
            >
              {{ test.referred_out ? 'Referred Out' : 'In-house' }}
            </span>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">Lab Sample ID:</h3>
            <p class="text-sm">{{ test.sample_code || 'N/A' }}</p>
          </div>
          <div class="mb-4">
            <div class="flex items-center gap-2 mb-1">
              <h3 class="text-sm font-semibold text-gray-600">Findings:</h3>
              <span v-if="test.lab_results.length > 1" class="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                Latest of {{ test.lab_results.length }} entries
              </span>
            </div>
            <div v-if="parseAnalyteBlob(test.lab_results.at(-1)?.results)" class="space-y-0.5">
              <div v-for="(entry, name) in parseAnalyteBlob(test.lab_results.at(-1)?.results)" :key="name" class="text-sm">
                <span class="font-medium text-gray-700">{{ name }}:</span>
                <span :class="entry.flag === 'H' || entry.flag === 'L' ? 'font-semibold text-red-700' : 'text-gray-800'">{{ entry.value }}</span>
                <span v-if="entry.unit" class="text-gray-400">{{ entry.unit }}</span>
                <span v-if="entry.flag === 'H' || entry.flag === 'L'" class="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700">{{ entry.flag }}</span>
              </div>
            </div>
            <p v-else class="text-sm">{{ test.lab_results.at(-1)?.results }}</p>
          </div>
          <div v-if="test.lab_results.at(-1)?.comments" class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">Lab Comments:</h3>
            <p class="text-sm italic text-gray-500">{{ test.lab_results.at(-1).comments }}</p>
          </div>

          <!-- Chain of custody — all 3 roles in one compact block -->
          <div class="mb-4 p-3 bg-blue-50 rounded-md border border-blue-100">
            <h3 class="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Chain of Custody</h3>
            <div class="space-y-1.5 text-sm">
              <div class="flex items-start gap-2">
                <span class="w-28 text-gray-500 shrink-0">Collected by:</span>
                <span class="font-medium text-gray-800">
                  {{ test.collecter?.Username || '—' }}
                </span>
              </div>
              <div class="flex items-start gap-2">
                <span class="w-28 text-gray-500 shrink-0">Conducted by:</span>
                <span class="font-medium text-gray-800">
                  {{ test.lab_results[0]?.filled_by?.Username || '—' }}
                </span>
              </div>
              <div class="flex items-start gap-2">
                <span class="w-28 text-gray-500 shrink-0">Reviewed by:</span>
                <span class="font-medium text-gray-800">
                  {{ test.reviewer?.Username || '—' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">Requesting Doctor:</h3>
            <p class="text-sm">{{ test.requested_by?.Username }}</p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
              Date &amp; Time Reviewed and Published:
            </h3>
            <p class="text-sm">{{ formatedDate(test.lab_results.at(-1)?.updated_at) }}</p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">Lab Report:</h3>
            <div
              v-if="test.lab_results.at(-1)?.url?.length"
              class="space-y-2"
            >
              <div
                v-for="(file, idx) in test.lab_results.at(-1).url"
                :key="idx"
                @click.stop="downloadFile(file.url, file.filename)"
                class="bg-gray-100 p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-200 transition"
              >
                <div class="flex items-center">
                  <svg class="w-6 h-6 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1 2H8l-1-2H5V5z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-sm font-medium truncate max-w-xs">{{ file.filename || 'Test Report' }}</span>
                </div>
                <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
            </div>
            <div v-else class="text-gray-500 text-sm">No Attachment</div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="currentTab === 'radiology-results'"
      class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto"
    >
      <div v-for="(test, index) in completedRadiologyResults" :key="index" class="grid grid-cols-2 gap-6">
        <div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
             Date Requested
            </h3>
            <p class="text-sm">{{ formatedDate(test.created_at) }}</p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">Image Type:</h3>
            <p class="text-sm">{{ test.imaging_type?.name}}</p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">Radiographer:</h3>
            <p class="text-sm">{{ test.radiology_results[0].filled_by?.Username }}</p>
          </div>
          <div class="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <h3 class="text-xs font-semibold text-gray-500 uppercase">Findings</h3>
            <p class="text-sm text-gray-800 mt-1 whitespace-pre-line">{{ test.radiology_results[0].findings || '—' }}</p>
          </div>
          <div v-if="test.radiology_results[0].recommendation" class="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <h3 class="text-xs font-semibold text-gray-500 uppercase">Recommendation</h3>
            <p class="text-sm text-gray-800 mt-1 whitespace-pre-line">{{ test.radiology_results[0].recommendation }}</p>
          </div>
          <div v-if="test.radiology_results[0].conclusion" class="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <h3 class="text-xs font-semibold text-gray-500 uppercase">Conclusion</h3>
            <p class="text-sm text-gray-800 mt-1 whitespace-pre-line">{{ test.radiology_results[0].conclusion }}</p>
          </div>
        </div>
        <div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
              Requesting Doctor:
            </h3>
            <p class="text-sm">{{ test.requested_by?.Username }}</p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
              Body Part/Region:
            </h3>
            <p class="text-sm">{{ test.body_part }}</p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
              Date & Time Reviewed and Published:
            </h3>
            <p class="text-sm">{{ formatedDate(test.updated_at) }}</p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
              Recommendation
            </h3>
            <p class="text-sm">
              {{ test.radiology_results[0].recommendation }}
            </p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
              Notes
            </h3>
            <p class="text-sm">
             {{ test.radiology_results[0].notes }}
            </p>
          </div>
          <div class="mb-4">
            <h3 class="text-sm font-semibold text-gray-600">
              Radiology Report
            </h3>
            <div
              v-if="test.radiology_results[0]?.url?.length"
              class="space-y-2"
            >
              <div
                v-for="(file, idx) in test.radiology_results[0]?.url"
                :key="idx"
                @click.stop="downloadFile(file.url, file.filename)"
                class="bg-gray-100 p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-gray-200 transition"
              >
                <div class="flex items-center">
                  <svg class="w-6 h-6 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1 2H8l-1-2H5V5z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-sm font-medium truncate max-w-xs">{{ file.filename || 'Test Report' }}</span>
                </div>
                <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
            </div>
            <div v-else class="text-gray-500 text-sm">No Attachment</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import LabDashboardPage from "@/views/pages/LabDashboardPage.vue";
import axios from "axios";

export default {
  name: "ProcedureResults",
  props: {
    existingLabTests: {
      type: Array,
      required: true,
    },
    existingRadiologyTests: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currentTab: "lab-results", // Default active tab
      showPreview: false,
      documentUrl: "@/assets/docs/productsticker.pdf",
    };
  },
  computed: {
    completedLabResults() {
      return this.existingLabTests.filter(
        (result) => result.status?.code === "ST-013"
      );
    },
    completedRadiologyResults() {
      return this.existingRadiologyTests.filter(
        (result) => result.status?.code === "ST-013"
      );
    },
  },
  mounted() {
    // This runs once when component is mounted
    console.log("existingLabTests:", JSON.parse(JSON.stringify(this.existingLabTests)));
    console.log("existingRadiologyTests:", JSON.parse(JSON.stringify(this.existingRadiologyTests)));
  },
  methods: {
    // Structured lab capture stores results as a JSON blob (`{ "Analyte": { value, unit, flag } }`);
    // older free-text results aren't JSON, so this returns null and the raw string is shown instead.
    parseAnalyteBlob(resultString) {
      if (!resultString || typeof resultString !== 'string') return null;
      try {
        const parsed = JSON.parse(resultString);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length) {
          return parsed;
        }
        return null;
      } catch {
        return null;
      }
    },

    formatedDate(dateString){
      const options = {
        weekday: 'long', // "Friday", "Monday"
        year: "numeric", // "2025"
        month: "long", // "January"
        day: "numeric", // "22"
        hour: "numeric", // "1 PM"
        minute: "numeric", // "43"
        hour12: true, // 12-hour time format
      };

      // parse as UTC
      const utcDate = new Date(dateString);

      const date = new Date(utcDate.getTime());

      return date.toLocaleString("en-US", options);
    },
    async downloadFile(fileUrl) {
      try {
        const response = await axios.get(fileUrl, {
          responseType: "blob",
        });

        // Extract filename from Content-Disposition header if available
        let fileName = "";
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          fileName = match ? match[1] : "";
        }

        // Fallback: Extract filename from URL if not found in headers
        if (!fileName) {
          fileName = fileUrl.split("/").pop().split("?")[0]; // Removes query params if any
        }

        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>